import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { X, Upload, X as XIcon } from 'lucide-react';
import { psp, pspm, upload } from '../assets/assets';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useSendQuote } from '../services/quote.service';

const ProjectSubmissionForm = ({ onClose }) => {
  const navigate = useNavigate();
  const { mutate: sendQuote, isLoading, isSuccess } = useSendQuote();
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    projectName: '',
    projectDescription: '',
    skills: [],
  });
  
  const [files, setFiles] = useState([]);
  const [isSkillsDropdownOpen, setIsSkillsDropdownOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // const [isUploadingFile, setIsUploadingFile] = useState(false); // REMOVE THIS

  const isUploadingFile = files.some(f => f.loading);

  const skillOptions = [
    { id: 1, name: 'Full Stack Development' },
    { id: 2, name: 'Frontend Development' },
    { id: 3, name: 'Backend Development' },
    { id: 4, name: 'E-commerce' },
    { id: 5, name: 'UI/UX Design' },
    { id: 6, name: 'Business Analytics' },
    { id: 7, name: 'Marketing' },
    { id: 8, name: 'Content Writing' },
    { id: 9, name: 'Digital Marketing' },
    { id: 10, name: 'SEO' },
    { id: 11, name: 'Other' },
  ];
  
  const filteredSkills = skillOptions.filter(skill => 
    skill.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const handleSkillSelect = (skill) => {
    if (!formData.skills.some(s => s.id === skill.id)) {
      setFormData(prevState => ({
        ...prevState,
        skills: [...prevState.skills, skill]
      }));
    }
    setSearchTerm('');
  };
  
  const handleRemoveSkill = (skillId) => {
    setFormData(prevState => ({
      ...prevState,
      skills: prevState.skills.filter(skill => skill.id !== skillId)
    }));
  };

  const uploadToCloudinary = async (file) => {
    const data = new FormData();
    data.append('file', file);
    data.append('upload_preset', 'foogles');
    const cloudName = 'djstk2ukm';

    try {
      const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: 'POST',
        body: data
      });
      const cloudData = await res.json();
      if (cloudData.secure_url) {
        return cloudData.secure_url;
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      console.error('Cloudinary upload error:', err);
      throw err;
    }
  };

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    await processFiles(selectedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      await processFiles(droppedFiles);
    }
  };

  const processFiles = async (newFilesList) => {
    setError('');
    
    // Add files to state immediately with loading: true
    const initialFiles = newFilesList.map(file => ({
      id: Math.random().toString(36).substring(2, 9),
      file,
      loading: true,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    
    setFiles(prevFiles => [...prevFiles, ...initialFiles]);

    // Upload each file
    for (const fileObj of initialFiles) {
      try {
        const url = await uploadToCloudinary(fileObj.file);
        setFiles(prevFiles => prevFiles.map(f => 
          f.id === fileObj.id ? { ...f, url, loading: false } : f
        ));
      } catch (err) {
        setError('One or more files failed to upload.');
        setFiles(prevFiles => prevFiles.filter(f => f.id !== fileObj.id));
      }
    }
  };

  const handleRemoveFile = (fileId) => {
    setFiles(prevFiles => {
      const file = prevFiles.find(f => f.id === fileId);
      if (file?.preview && !file.preview.startsWith('http')) {
        URL.revokeObjectURL(file.preview);
      }
      return prevFiles.filter(f => f.id !== fileId);
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!formData.fullName || !formData.email || !formData.projectName || !formData.projectDescription) {
      setError('Please fill in all required fields.');
      return;
    }

    if (isUploadingFile) {
      setError('Please wait for files to finish uploading.');
      return;
    }

    sendQuote({
      ...formData,
      skills: formData.skills.map(s => s.name),
      files: files.map(f => f.url)
    }, {
      onSuccess: () => {
        console.log('Submission successful');
      },
      onError: (err) => {
        setError(err?.response?.data?.error || 'Failed to submit project request');
      }
    });
  };

  return (
    <>
    <Header/>
    <div className="w-full min-h-screen bg-[#F5F5F5] font-['Poppins'] md:flex">
      {/* Mobile header with close button */}
      <div className="md:hidden flex justify-between items-center p-4 bg-white">
        <Link to={"/"}>
      <h3 className="text-xl font-medium text-[#174568]">
            Hello<span className="text-[#7295B8]">cer</span>
          </h3>

          </Link>
        <button 
          onClick={() => navigate('/')} 
          className="text-gray-700"
          aria-label="Back"
        >
           <Link to={"/"}>
          <X size={24} className="text-gray-800" />
          </Link>
        </button>
      </div>

       {/* Mobile view - show image at the top */}
       <div className="md:hidden h-[270px] w-[88%] mx-auto overflow-hidden">
        <img 
          src={pspm} 
          alt="Person working on keyboard" 
          className="w-full h-full object-cover"
        />
      </div>

      {/* Desktop view - split screen */}
      <div className="md:w-1/2 p-6 md:p-10">
        {/* Desktop logo (hidden on mobile) */}

        <Link to={"/"}>
        <h3 className="hidden md:block text-xl font-medium text-[#174568]">
            Hello<span className="text-[#7295B8]">cer</span>
          </h3>
          </Link>

        <h1 className="text-[#174568] text-[40px] font-bold my-8">
          Tell us what<br />
          you need done
        </h1>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
          
          {isSuccess && (
            <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded-md">
              <p className="font-bold">Success!</p>
              <p>Your project request has been submitted. Our team will review it and get back to you via email.</p>
              <button 
                onClick={() => navigate('/')}
                className="mt-3 text-sm underline font-medium"
              >
                Back to Chat 
              </button>
            </div>
          )}

          {!isSuccess && (
            <>
          <div>
            <label htmlFor="fullName" className="block text-gray-700 mb-2">Full name *</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              required
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder="Enter your full name"
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-gray-700 mb-2">Email address *</label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Enter your email"
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="projectName" className="block text-gray-700 mb-2">Project name *</label>
            <input
              type="text"
              id="projectName"
              name="projectName"
              required
              value={formData.projectName}
              onChange={handleInputChange}
              placeholder="Enter project name"
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label htmlFor="projectDescription" className="block text-gray-700 mb-2">Project description</label>
            <textarea
              id="projectDescription"
              name="projectDescription"
              value={formData.projectDescription}
              onChange={handleInputChange}
              placeholder="Enter project description"
              rows="6"
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          
          <div className="relative">
            <label htmlFor="skills" className="block text-gray-700 mb-2">Skills</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.skills.map(skill => (
                <div key={skill.id} className="flex items-center bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                  {skill.name}
                  <button 
                    type="button" 
                    onClick={() => handleRemoveSkill(skill.id)}
                    className="ml-2 text-blue-800 hover:text-[#174568]"
                  >
                    <XIcon size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="relative">
              <input
                type="text"
                id="skills"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setIsSkillsDropdownOpen(true)}
                placeholder="Search for skills"
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {isSkillsDropdownOpen && searchTerm && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {filteredSkills.length > 0 ? (
                    filteredSkills.map(skill => (
                      <div
                        key={skill.id}
                        onClick={() => {
                          handleSkillSelect(skill);
                          setIsSkillsDropdownOpen(false);
                        }}
                        className="p-2 hover:bg-gray-100 cursor-pointer"
                      >
                        {skill.name}
                      </div>
                    ))
                  ) : (
                    <div className="p-2 text-gray-500">No matching skills found</div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          <div>
            <label className="block text-gray-700 mb-2">Attachments</label>
            <div 
              className="w-full p-3 border border-gray-300 rounded-md bg-white"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className="flex items-center space-x-3 mb-3 cursor-pointer" onClick={() => fileInputRef.current.click()}>
             
                <img src={upload}/>
                <span className="text-gray-500 text-sm" >
                  Drag & drop any images or documents that might be helpful in explaining your brief here. (Max 25 MB)
                </span>
              </div>
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                className="hidden"
              />
              
              {files.length > 0 && (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {files.map(file => (
                    <div key={file.id} className="relative group">
                      <div className="border rounded-md overflow-hidden bg-gray-100 w-full aspect-square relative">
                        {file.loading && (
                          <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex flex-col items-center justify-center p-2">
                             <div className="w-6 h-6 border-2 border-[#174568]/30 border-t-[#174568] rounded-full animate-spin mb-1"></div>
                             <span className="text-[10px] font-bold text-[#174568]">UPLOADING</span>
                          </div>
                        )}
                        {file.preview ? (
                          <img 
                            src={file.preview} 
                            alt={file.file.name} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center p-2">
                            <div className="text-center">
                              <div className="text-gray-500 text-xs truncate max-w-[80px]">{file.file.name}</div>
                              <div className="text-gray-400 text-xs">{(file.file.size / 1024).toFixed(0)} KB</div>
                            </div>
                          </div>
                        )}
                      </div>
                      {!file.loading && (
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(file.id)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 z-20"
                        >
                          <XIcon size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-3 px-4 bg-[#174568] text-white font-medium rounded-md hover:bg-gray-800 transition duration-200 flex items-center justify-center gap-2 ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Submitting...</span>
              </>
            ) : (
              'Continue'
            )}
          </button>
          </>
          )}
        </form>
      </div>
      
      {/* Image section - only visible on desktop */}
      <div className="hidden md:block max-h-[135vh] object-cover md:w-1/2 bg-gray-200">
        <img 
          src={psp}
          alt="Person working on keyboard" 
          className="w-full h-full object-cover"
        />
      </div>
      
     
    </div>
    <Footer/>
    </>
  );
};

export default ProjectSubmissionForm;